/**
 * CreditCard entity class
 */

import { CREDIT_CARD_TYPES } from 'app/core/constants'

/**
 * CreditCardFactory
 * returns CreditCard entity class
 *
 * @class CreditCardFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.CreditCard} class
 *
 * @ngdoc factory
 * @name mwApp.factory:CreditCardFactory
 */
/* @ngInject */
export default function CreditCardFactory (Type, Entity, onOffMixin) {
  /* === CreditCard class === */

  /**
   * CreditCard model
   *
   * @class CreditCard
   * @classdesc CreditCard model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.CreditCard} CreditCard entity
   */
  function CreditCard () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  CreditCard.prototype = Object.create(Entity.prototype)
  CreditCard.prototype.constructor = CreditCard

  /* === CreditCard instance methods & fields === */

  Object.assign(CreditCard.prototype, {

    /**
     * instance fields
     *
     * @memberof models.CreditCard#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      address: String,
      city: String,
      country: String,
      creditCardNumber: String,
      creditCardType: String,
      customBillingAddress: false,
      defaultPaymentMethod: Boolean,
      email: String,
      enabled: true,
      expirationDate: {
        expirationMonth: String,
        expirationYear: String
      },
      firstName: String,
      lastName: String,
      status: String,
      storageType: Type(String('AUTHORIZE_NET')), // LOCAL, AUTHORIZE_NET
      phone: String,
      state: String,
      subscriber: 'Subscriber',
      transactions: Number,
      user: 'User',
      zipcode: String,
      redirectUrl: String
    },

    /**
     * url template
     *
     * @memberof models.CreditCard#
     * @type {String}
     */
    _urlTemplate: 'api/subscribers/{{::subscriber.id}}/creditcards/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.CreditCard#
     * @type {String}
     */
    _publicUrlTemplate: 'subscribers/{{::subscriber.id}}/activation{{::id ? "/cards/" : "#cards"}}{{::id}}',

    /**
     * Prepare entity, fill fields
     *
     * @memberof models.CreditCard#
     * @override
     */
    _prepare: function () {
      this.expirationDate = this.expirationDate || null // Note: may be used in other!!

      // prepend card number with tree stars, like 'hidden'
      if (this.creditCardNumber) {
        this.creditCardNumber = this.creditCardNumber.replace(/X/g, '*')
      }
    },

    /**
     * Fill billing address from subscriber, if customBillingAddress is false
     *
     * @param {models.CreditCard} [from] - get billing address from another credit card
     * @memberof models.CreditCard#
     */
    _fillAddress: function (from) {
      if (from) {
        this.address = from.address
        this.city = from.city
        this.country = from.country
        this.email = from.email
        this.firstName = from.firstName
        this.lastName = from.lastName
        this.phone = from.phone
        this.state = from.state
        this.zipcode = from.zipcode
        return
      }

      if (!this.customBillingAddress && this.subscriber) {
        this.address = this.subscriber.address
        this.city = this.subscriber.city
        this.country = this.subscriber.country
        this.email = this.subscriber.email
        this.firstName = this.subscriber.firstname
        this.lastName = this.subscriber.lastname
        this.phone = this.subscriber.phone
        this.state = this.subscriber.state
        this.zipcode = this.subscriber.zipcode
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.CreditCard#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      var ret = Entity.prototype._object.apply(this, arguments)

      // change * to X in card number
      if (ret.creditCardNumber) {
        ret.creditCardNumber = ret.creditCardNumber.indexOf('*') !== -1
          ? ret.creditCardNumber.replace(/\*/g, 'X')
          : ret.creditCardNumber.replace(/\s+/g, '')
      }

      // uppercase for zip code
      if (ret.zipcode) {
        ret.zipcode = ret.zipcode.toUpperCase()
      }

      return ret
    },

    /**
     * Return list of available years for credit card
     *
     * @memberof models.CreditCard#
     * @return {String[]}
     */
    _getExpYears: function () {
      var cur = new Date().getFullYear()
      var delta = 10
      var isNew = this._isNew()
      var ret = []

      for (var y = isNew ? cur : cur - delta; y <= cur + delta; y++) {
        ret.push('' + y)
      }

      if (this.expirationDate && this.expirationDate.expirationYear && ret.indexOf(this.expirationDate.expirationYear) === -1) {
        ret.push(this.expirationDate.expirationYear)
        ret.sort()
      }

      return ret
    },

    /**
     * Get card type
     *
     * @memberof models.CreditCard#
     * @param {String} w - 't' => text, 'c' => className
     * @return {String}
     */
    _type: function (w) {
      var type = this.creditCardType
      var number = this.creditCardNumber ? this.creditCardNumber.replace(/\s+/g, '') : null

      if (!type) {
        type = 'UNKNOWN'
        for (var t in CREDIT_CARD_TYPES) {
          if (CREDIT_CARD_TYPES[t].re && CREDIT_CARD_TYPES[t].re.test(number)) {
            type = t
            break
          }
        }
      }

      return w === 'c' ? CREDIT_CARD_TYPES[type].className : type
    }

  }, onOffMixin)

  /* === CreditCard static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(CreditCard, Entity)
  CreditCard._name = 'CreditCard'
  CreditCard._publicName = 'Credit Card'
  CreditCard.init()

  // permissions
  CreditCard.access = {
    view: 'viewSubscriberActivationCreditCard createSubscriptionPayment |',
    create: 'createSubscriberActivationCreditCard',
    edit: 'editSubscriberActivationCreditCard',
    delete: 'deleteSubscriberActivationCreditCard'
  }

  // extra
  CreditCard.extra = {
    defaultStorageType: String, // LOCAL,AUTHORIZE_NET
    allowedStorageTypes: [String]
  }

  return CreditCard
}
